import { action, atom, readonly } from '@reatom/framework'

import { Array, AsyncStateN, pipe } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { newTxBufferedEvents } from '@root/entities/realtime/atoms/new-tx-event'
import { Trade } from '@root/entities/trade'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { TableFilter, type DataTableModel } from '@root/shared/ui-kit'

import type { Atom } from '@reatom/framework'
import type { Address } from '@root/entities/address/Address'
import type { PairAddress } from '@root/entities/pair'
import type { TradeTableColumnId } from '@root/features/trade/tables/trades-table/atoms'

export const tradesPausedAtom = atom(false, 'tradesPausedAtom')
export const reatomTradesTableRealtime = ({
  pairAddress,
  tableModel,
  wallets,
}: {
  pairAddress: PairAddress | undefined
  wallets: Atom<Array<Address> | undefined>
  tableModel: DataTableModel<Trade.Trade, TradeTableColumnId, object, undefined>
}) => {
  // Used when table is paused to store trades from WS
  const tradesBufferAtom = atom<Array<Trade.Trade>>([], 'tradesBufferAtom')

  const ageSortingAtom = atom((ctx) => {
    const sorting = ctx.spy(tableModel.sorting)
    if (!sorting || sorting.sortColumn !== 'timestamp') {
      return 'desc'
    }
    return sorting.sortDirection
  }, 'ageSortingAtom')

  const upsertNewTxsAction = action((ctx, newTxs: Array<Trade.Trade>) => {
    const ageSorting = ctx.get(ageSortingAtom)

    const order =
      ageSorting === 'desc' ? Trade.cmpByTimestampDesc : Trade.cmpByTimestampAsc
    const sortedTxs = pipe(newTxs, Array.sort(order))

    if (ageSorting === 'desc') {
      tableModel.upsertItems(ctx, sortedTxs, {
        applyClientSideFilters: false,
        applyClientSideSorting: false,
        insertMode: 'prepend',
      })
      return
    }
    const data = ctx.get(tableModel).value
    // If not reached the end of list
    if (!data || data.hasNext) {
      return
    }

    tableModel.upsertItems(ctx, sortedTxs, {
      applyClientSideFilters: false,
      applyClientSideSorting: false,
      insertMode: 'append',
    })
  }, 'upsertNewTxsAction')

  const composedFilterAtom = atom((ctx) => {
    const filters = ctx.spy(tableModel.filters)
    return TableFilter.composeFilters(filters)
  }, 'composedFilterAtom')

  const initializeAction = action((ctx) => {
    const network = Network.toSupportedNetwork(ctx.get(selectedNetworkAtom))
    const { chartWebsocket } = ctx.get(dependenciesAtom)

    const unsubscribeTxEvent = newTxBufferedEvents.onCall((ctx, newTxs) => {
      const state = ctx.get(tableModel)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }

      const composedFilter = ctx.get(composedFilterAtom)

      const isApplicable = (newTx: Trade.Trade) => {
        // The buffer carries every pool anybody on this page is listening to,
        // and that is no longer only this one: the explore columns stream the
        // trades of the pools they show, and the strip above them keeps that
        // going after you open a pair. Without this the table filled with
        // other pools' trades, out of order and out of nowhere.
        //
        // A trade that does not say which pool it belongs to is let through.
        // Dropping those would be trading a leak for a dead table, and the
        // first attempt at this filter did look like it had done exactly that.
        if (
          pairAddress &&
          newTx.pairAddress &&
          newTx.pairAddress !== pairAddress
        ) {
          return false
        }

        const wallets_ = ctx.get(wallets)
        if (wallets_ && !wallets_.includes(newTx.maker.address)) {
          return false
        }

        // Events with quote token are sent when there is no pair
        if (newTx.quoteCurrency && pairAddress) {
          return false
        }

        if (composedFilter) {
          return TableFilter.test(composedFilter, tableModel.columns)(newTx)
        }

        return true
      }

      const txs = newTxs.filter(isApplicable)

      if (txs.length === 0) {
        return
      }

      const ageSorting = ctx.get(ageSortingAtom)
      // If not reached the end of list
      if (ageSorting === 'asc' && state.value.hasNext) {
        return
      }

      const paused = ctx.get(tradesPausedAtom)

      if (!paused) {
        upsertNewTxsAction(ctx, txs)
        return
      }

      const currentBuffer = ctx.get(tradesBufferAtom)
      const updatedBuffer = pipe([...currentBuffer, ...txs])

      tradesBufferAtom(ctx, updatedBuffer)
      return
    })

    if (pairAddress) {
      chartWebsocket.subscribeNewTxAction(ctx, {
        pairAddress,
        network,
      })
    }

    return () => {
      unsubscribeTxEvent()
      if (pairAddress) {
        chartWebsocket.unsubscribeNewTxAction(ctx, {
          pairAddress,
          network,
        })
      }
    }
  }, 'initializeAction')

  const setPausedAction = action((ctx, paused: boolean) => {
    tradesPausedAtom(ctx, paused)

    if (!paused) {
      const buffer = ctx.get(tradesBufferAtom)
      upsertNewTxsAction(ctx, buffer)
      tradesBufferAtom(ctx, [])
    }
  }, 'setPausedAction')

  const buffedCountAtom = atom((ctx) => {
    const buffer = ctx.spy(tradesBufferAtom)
    return buffer.length
  }, 'buffedCountAtom')

  return {
    paused: tradesPausedAtom.pipe(readonly),
    setPaused: setPausedAction,
    buffedCount: buffedCountAtom,
    initialize: initializeAction,
  }
}
